import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Link2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/store/authStore';
import { supabase } from '@/services/db/supabase';

// Minimal stub: tenant_admin sets a PROJO project UUID on a MOZAIC project.
// Session 49 — full settings UI comes later. Protected by role gate.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface ProjectRow {
  id: string;
  name: string;
  code: string | null;
  projo_project_id: string | null;
}

export default function ProjectSettingsPage() {
  const userRole = useAuthStore(s => s.userRole);
  const queryClient = useQueryClient();
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [projoId, setProjoId] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const canEdit = userRole === 'tenant_admin' || userRole === 'platform_admin';

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['settings_projects'],
    queryFn: async (): Promise<ProjectRow[]> => {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, code, projo_project_id')
        .eq('is_deleted', false)
        .order('name');
      if (error) throw error;
      return (data ?? []) as ProjectRow[];
    },
    enabled: canEdit,
  });

  const selected = projects.find(p => p.id === selectedProjectId);

  const onSelectProject = (id: string) => {
    setSelectedProjectId(id);
    const p = projects.find(x => x.id === id);
    setProjoId(p?.projo_project_id ?? '');
    setMessage(null);
  };

  const onSave = async () => {
    if (!selected) return;
    const trimmed = projoId.trim();
    if (trimmed && !UUID_RE.test(trimmed)) {
      setMessage({ kind: 'err', text: 'Must be a valid UUID or empty to unlink.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const { error } = await supabase
        .from('projects')
        .update({ projo_project_id: trimmed || null })
        .eq('id', selected.id);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['settings_projects'] });
      setMessage({
        kind: 'ok',
        text: trimmed
          ? `${selected.name} is now linked to PROJO project ${trimmed}`
          : `${selected.name} unlinked from PROJO.`,
      });
    } catch (err) {
      setMessage({ kind: 'err', text: err instanceof Error ? err.message : 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Project settings require tenant_admin or platform_admin.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl p-6">
      <div className="mb-4 flex items-center gap-2">
        <Link2 className="h-5 w-5 text-mozaic-teal" />
        <h1 className="text-xl font-semibold text-mozaic-navy">Project PROJO Links</h1>
      </div>
      <p className="mb-6 text-sm text-muted-foreground">
        Link a MOZAIC project to its PROJO counterpart to enable workforce
        registry import and certification validation in the eDSR.
      </p>

      <Card>
        <CardContent className="space-y-4 pt-6">
          {isLoading && (
            <div className="flex h-24 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-mozaic-teal" />
            </div>
          )}

          {!isLoading && (
            <>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  MOZAIC Project
                </Label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={selectedProjectId}
                  onChange={e => onSelectProject(e.target.value)}
                >
                  <option value="">Select a project…</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.code ? ` (${p.code})` : ''}
                      {p.projo_project_id ? ' — linked' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  PROJO Project ID (UUID)
                </Label>
                <Input
                  value={projoId}
                  onChange={e => setProjoId(e.target.value)}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  disabled={!selected}
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty to unlink. Find the UUID in PROJO under the project
                  detail page.
                </p>
              </div>

              {message && (
                <p
                  className={`text-sm ${
                    message.kind === 'ok' ? 'text-mozaic-teal' : 'text-mozaic-red'
                  }`}
                >
                  {message.text}
                </p>
              )}

              <Button
                className="bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
                onClick={onSave}
                disabled={!selected || saving}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Link'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
